import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../lib/api";
import { AdminNav } from "../components/AdminNav";
import { Loader2, ChevronRight, Users, FileText, ShoppingBag, Repeat } from "lucide-react";

const LS_TOKEN = "bnm-admin-token";

const STAGE_TINT = [
  "from-sage/25 to-sage/5",
  "from-gold/20 to-gold/5",
  "from-gold-deep/25 to-gold-deep/5",
  "from-ink/15 to-ink/5",
  "from-terracotta/20 to-terracotta/5",
  "from-gold-deep/30 to-gold-deep/10",
];

export default function AdminJourney() {
  const [token] = useState(() => localStorage.getItem(LS_TOKEN) || "");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) { setErr("Please sign in at /admin first."); setLoading(false); return; }
    axios.get(`${API}/admin/crm/journey`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e.response?.status === 401 ? "Please sign in at /admin first." : "Could not load the journey."))
      .finally(() => setLoading(false));
  }, [token]);

  if (err) return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-ivory-100 px-5 text-center">
      <div><p className="text-ink-soft" data-testid="journey-error">{err}</p><Link to="/admin" className="mt-3 inline-block text-gold-deep underline">Go to /admin</Link></div>
    </div>
  );

  const stages = data?.stages || [];
  const m = data?.metrics || {};
  const peak = Math.max(1, ...stages.map((s) => s.count || 0));
  const metricCards = [
    { label: "Total contacts", value: m.total_contacts, icon: Users },
    { label: "Immersion applicants", value: m.applicants, icon: FileText },
    { label: "Orders placed", value: m.orders, icon: ShoppingBag },
    { label: "Active subscriptions", value: m.active_subscriptions, icon: Repeat },
  ];

  return (
    <div className="min-h-[100dvh]" style={{ background: "linear-gradient(180deg,#F3EFEA 0%,#ECE7DE 100%)" }} data-testid="admin-journey">
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8">
        <AdminNav />
        <div>
          <h1 className="font-display text-3xl text-ink">Client Journey</h1>
          <p className="mt-1 text-sm text-ink-muted">How people move through the ecosystem, from free chapter to offerings.</p>
        </div>

        {loading ? (
          <p className="mt-8 flex items-center gap-2 text-sm text-ink-muted"><Loader2 className="h-4 w-4 animate-spin" /> Loading journey…</p>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="journey-metrics">
              {metricCards.map((c) => {
                const Icon = c.icon;
                return (
                  <div key={c.label} className="rounded-2xl bg-ivory-50 p-4 shadow-sm ring-1 ring-ivory-400/60">
                    <Icon className="h-4 w-4 text-gold-deep" />
                    <p className="mt-2 font-display text-2xl text-ink">{c.value ?? 0}</p>
                    <p className="text-[11px] uppercase tracking-[0.12em] text-ink-muted">{c.label}</p>
                  </div>
                );
              })}
            </div>

            <div className="mt-7 space-y-3" data-testid="journey-stages">
              {stages.map((s, i) => {
                const pct = Math.round(((s.count || 0) / peak) * 100);
                const body = (
                  <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-r ${STAGE_TINT[i % STAGE_TINT.length]} p-5 shadow-sm ring-1 ring-ivory-400/60`}>
                    <div className="absolute inset-y-0 left-0 bg-ink/[0.03]" style={{ width: `${Math.max(pct, 6)}%` }} />
                    <div className="relative flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-ink/90 font-display text-sm text-ivory-50">{i + 1}</span>
                        <div>
                          <p className="font-display text-lg text-ink">{s.label}</p>
                          <p className="text-xs text-ink-muted">{s.desc}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-display text-2xl text-ink" data-testid={`journey-count-${s.key}`}>{s.count ?? 0}</p>
                        </div>
                        {s.type && <ChevronRight className="h-4 w-4 text-ink-muted" />}
                      </div>
                    </div>
                  </div>
                );
                return s.type ? (
                  <Link key={s.key} to={`/admin/crm?type=${s.type}`} data-testid={`journey-stage-${s.key}`} className="block transition-transform hover:-translate-y-0.5">{body}</Link>
                ) : (
                  <div key={s.key} data-testid={`journey-stage-${s.key}`}>{body}</div>
                );
              })}
            </div>
            <p className="mt-4 text-xs text-ink-muted">Tap a stage with an arrow to open those contacts in the CRM.</p>
          </>
        )}
      </div>
    </div>
  );
}
